import type { ReactElement } from 'react';
import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { useMutation } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	ADMIN_ANNOUNCEMENT_BANNER_TURN_OFF_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import type { FlagsStateContainer } from '@confluence/flags';
import { ErrorDisplay } from '@confluence/error-boundary';

import { ArchiveIcon } from '../img/ArchiveIcon';
import {
	ConfluenceAdminAnnouncementBannerStatusType,
	type UpdateAdminAnnouncementBannerMutation as UpdateAdminAnnouncementBannerMutationType,
	type UpdateAdminAnnouncementBannerMutationVariables,
} from '../../queries/__types__/UpdateAdminAnnouncementBannerMutation';
import { UpdateAdminAnnouncementBannerMutation } from '../../queries/UpdateAdminAnnouncementBanner.graphql';
import { MutationSource, MutationType, MutationUpdateReason } from '../../AABForm/AABFormConstants';
import { handleErrorsM2, handleUnexpectedMutationError } from '../../handleErrors';
import { AdminAnnouncementBannerQuery } from '../../queries/AdminAnnouncementBannerQuery.graphql';
import {
	MutationStatus,
	getMutationAnalyticsEventPayload,
} from '../../getMutationAnalyticsEventPayload';
import { getScheduledDateStringFromDate } from '../../SchedulingFields/SchedulingFieldsUtils';
import { getBrowserTimezone } from '../../SchedulingFields/TimeUtils';

import type { QuickActionCommonProps } from './QuickActionBaseComponents';
import { QuickActionBaseComponent, QuickActionName } from './QuickActionBaseComponents';

const i18n = defineMessages({
	archiveSuccessFlagTitle: {
		id: 'admin-announcement-banner.archive.success.title',
		defaultMessage: '{ announcementTitle } archived',
		description: 'The title of the flag that shows when the user successfully archives the banner.',
	},
});

type ArchiveQuickActionProps = QuickActionCommonProps & {
	flags: FlagsStateContainer;
	title: string | ReactElement;
	scheduledStartDate: string;
	status: ConfluenceAdminAnnouncementBannerStatusType;
};

export const ArchiveQuickAction = ({
	id,
	title,
	scheduledStartDate,
	status,
	flags,
}: ArchiveQuickActionProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const [updateBanner, { error }] = useMutation<
		UpdateAdminAnnouncementBannerMutationType,
		UpdateAdminAnnouncementBannerMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		UpdateAdminAnnouncementBannerMutation,
		{
			onCompleted: (data) => {
				if (data.updateAdminAnnouncementBanner?.success === false) {
					handleErrorsM2({
						createAnalyticsEvent,
						experienceTracker,
						flags,
						formatMessage,
						data,
						mutationUpdateReason: MutationUpdateReason.ARCHIVE,
						mutationSource: MutationSource.TABLE,
					});
					return null;
				}
				void flags.showFlag({
					type: 'success-circle',
					title: formatMessage(i18n.archiveSuccessFlagTitle, {
						announcementTitle: title,
					}),
				});
				const bannerSettings = data.updateAdminAnnouncementBanner?.adminAnnouncementBannerSetting;
				createAnalyticsEvent(
					getMutationAnalyticsEventPayload({
						mutationType: MutationType.UPDATE,
						updateReason: MutationUpdateReason.ARCHIVE,
						mutationStatus: MutationStatus.SUCCESS,
						data: bannerSettings,
					}),
				).fire();
				experienceTracker.succeed({
					name: ADMIN_ANNOUNCEMENT_BANNER_TURN_OFF_EXPERIENCE,
					attributes: {
						source: MutationSource.TABLE,
					},
				});
			},
			onError: (updateMutationUnexpectedError: ApolloError) => {
				handleUnexpectedMutationError({
					createAnalyticsEvent,
					experienceTracker,
					flags,
					formatMessage,
					unexpectedError: updateMutationUnexpectedError,
					mutationUpdateReason: MutationUpdateReason.ARCHIVE,
					mutationSource: MutationSource.TABLE,
				});
			},
			refetchQueries: [{ query: AdminAnnouncementBannerQuery }],
		},
	);

	const onClickArchiveAction = () => {
		experienceTracker.start({
			name: ADMIN_ANNOUNCEMENT_BANNER_TURN_OFF_EXPERIENCE,
			attributes: {
				source: MutationSource.TABLE,
			},
		});

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'adminAnnouncementBannerHomePage',
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'adminAnnouncementBannerArchive',
			},
		}).fire();

		const isScheduled = status === ConfluenceAdminAnnouncementBannerStatusType.SCHEDULED;
		// Validation for whether current banner was created before M2 FF is on.
		// M2 active banners always have a non-null scheduledStartDate, so M1 active banners can be identified
		// by checking for a null scheduledStartDate (since scheduling is a M2 feature).
		const isM1ActiveBanner =
			status === ConfluenceAdminAnnouncementBannerStatusType.PUBLISHED && !scheduledStartDate;

		return void updateBanner({
			variables: {
				announcementBanner: {
					id,
					status: ConfluenceAdminAnnouncementBannerStatusType.SAVED,
					scheduledStartTime:
						isScheduled || isM1ActiveBanner
							? null
							: getScheduledDateStringFromDate(new Date(scheduledStartDate)),
					scheduledEndTime:
						isScheduled || isM1ActiveBanner ? null : getScheduledDateStringFromDate(new Date()),
					scheduledTimeZone:
						isScheduled || isM1ActiveBanner ? null : getBrowserTimezone()?.value.zone || '',
				},
			},
		});
	};

	return (
		<>
			{error && <ErrorDisplay error={error} />}
			<QuickActionBaseComponent
				icon={ArchiveIcon()}
				onClick={onClickArchiveAction}
				actionName={QuickActionName.ARCHIVE}
			/>
		</>
	);
};
